import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw, ArrowLeft } from "lucide-react";

// RouteErrorBoundary — generic render-phase guard for a whole route, so an
// unexpected runtime error inside one page shows a recovery card instead of
// unmounting the tree and leaving the bare app background behind.
//
// CalcErrorBoundary is the calculator-specific sibling of this component;
// its copy ("Reload this calculator", "Back to Categories") only makes
// sense inside a calculator, which is why this one exists separately
// rather than that one being reused with misleading wording.
//
// Props:
//   - label:     page name shown in the recovery card ("Owner Dashboard")
//   - backTo:    route for the escape-hatch link (default "/home")
//   - backLabel: text for that link (default "Back to Home")
//   - children:  the protected sub-tree

const DIAG_KEY = "rsc.route.errors.v1";
const DIAG_MAX = 20;

function _persist(envelope) {
  try {
    const list = JSON.parse(localStorage.getItem(DIAG_KEY) || "[]");
    list.unshift(envelope);
    while (list.length > DIAG_MAX) list.pop();
    localStorage.setItem(DIAG_KEY, JSON.stringify(list));
  } catch {
    /* localStorage may be sealed in private browsing — ignore */
  }
}

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const envelope = {
      at:        new Date().toISOString(),
      label:     this.props.label || "Unknown page",
      message:   String(error?.message || error),
      stack:     String(error?.stack || ""),
      component: String(info?.componentStack || ""),
      ua:        navigator.userAgent,
    };
    console.error("[RigSentinel] Route render error:", envelope);
    _persist(envelope);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ error: null, info: null, showDetails: false });
    if (typeof this.props.onReset === "function") {
      try { this.props.onReset(); } catch { /* best effort */ }
    }
  };

  render() {
    const { error, info, showDetails } = this.state;
    if (!error) return this.props.children;

    const label = this.props.label || "This page";
    const backTo = this.props.backTo || "/home";
    const backLabel = this.props.backLabel || "Back to Home";

    return (
      <div data-testid="route-error-boundary" className="max-w-2xl mx-auto px-4 py-8">
        <div className="card-rs p-5"
             style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-1">
              <AlertTriangle className="w-6 h-6 text-[#FCA5A5]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FCA5A5]">
                Page error
              </div>
              <h2 className="text-[18px] font-semibold text-[var(--rs-text-1)] mt-0.5">
                Something went wrong on this page.
              </h2>
              <p className="text-[12px] text-[var(--rs-text-2)] mt-2 leading-snug">
                <strong>{label}</strong> hit an unexpected runtime error. The
                rest of RigSentinel is unaffected — your saved profiles and any
                in-progress work are intact.
              </p>
              <p className="text-[11px] text-[var(--rs-text-3)] mt-2 leading-snug">
                A diagnostic log has been written to local storage.
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button data-testid="route-error-reload" type="button"
                        onClick={this.handleReload}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium"
                        style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
                  <RotateCcw className="w-4 h-4" /> Try again
                </button>
                <Link data-testid="route-error-back" to={backTo}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px]"
                      style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
                  <ArrowLeft className="w-4 h-4" /> {backLabel}
                </Link>
                <button data-testid="route-error-details-toggle" type="button"
                        onClick={() => this.setState({ showDetails: !showDetails })}
                        className="text-[11px] underline text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] ml-auto">
                  {showDetails ? "Hide details" : "Show diagnostic details"}
                </button>
              </div>

              {showDetails && (
                <pre data-testid="route-error-details"
                     className="mt-4 p-3 text-[10.5px] font-mono whitespace-pre-wrap overflow-auto rounded-md"
                     style={{
                       background: "rgba(10,11,14,0.85)",
                       border: "1px solid var(--rs-border)",
                       color: "var(--rs-text-2)",
                       maxHeight: 320,
                     }}>
                  {String(error?.message || error)}{"\n\n"}
                  {String(error?.stack || "")}{"\n\n"}
                  {String(info?.componentStack || "")}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
